import { useEffect, useState, useRef, FormEvent, type DragEvent, type ChangeEvent } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Trash2, Upload, ImagePlus, LogOut, Lock, FolderPlus, Folder, ChevronDown, ChevronUp, X, FileImage } from 'lucide-react'
import Layout from '../components/Layout'
import { supabase, type GalleryImage, type GalleryAlbum } from '../lib/supabase'

export default function AdminGallery() {
  const [session, setSession] = useState<null | { user: { email: string } }>(null)
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [loginError, setLoginError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)

  const [albums, setAlbums] = useState<GalleryAlbum[]>([])
  const [images, setImages] = useState<GalleryImage[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })
  const [caption, setCaption] = useState('')
  const [selectedAlbumId, setSelectedAlbumId] = useState('')
  const [newAlbumName, setNewAlbumName] = useState('')
  const [newAlbumDesc, setNewAlbumDesc] = useState('')
  const [showNewAlbum, setShowNewAlbum] = useState(false)
  const [message, setMessage] = useState('')
  const [expandedAlbums, setExpandedAlbums] = useState<Set<string>>(new Set())
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [fileInputKey, setFileInputKey] = useState(0)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setSession({ user: { email: data.session.user.email || '' } })
    })
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (newSession) setSession({ user: { email: newSession.user.email || '' } })
      else setSession(null)
    })
    return () => { listener.subscription.unsubscribe() }
  }, [])

  useEffect(() => { if (session) fetchAll() }, [session])

  const fetchAll = async () => {
    setLoading(true)
    const [{ data: albumsData }, { data: imagesData }] = await Promise.all([
      supabase.from('gallery_albums').select('*').order('created_at', { ascending: false }),
      supabase.from('gallery_images').select('*').order('created_at', { ascending: false }),
    ])
    if (albumsData) setAlbums(albumsData as GalleryAlbum[])
    if (imagesData) setImages(imagesData as GalleryImage[])
    setLoading(false)
  }

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault()
    setLoginError('')
    setLoginLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email: loginEmail, password: loginPassword })
    if (error) setLoginError('Błędny email lub hasło.')
    setLoginLoading(false)
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setSession(null)
  }

  const handleCreateAlbum = async (e: FormEvent) => {
    e.preventDefault()
    if (!newAlbumName.trim()) return
    const { error } = await supabase.from('gallery_albums').insert({
      name: newAlbumName.trim(),
      description: newAlbumDesc.trim(),
    })
    if (!error) {
      setNewAlbumName('')
      setNewAlbumDesc('')
      setShowNewAlbum(false)
      fetchAll()
    }
  }

  const addFiles = (files: FileList | null) => {
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'))
      if (imageFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...imageFiles])
        setMessage('')
      }
    }
  }

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files)
    setFileInputKey((k) => k + 1)
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    addFiles(e.dataTransfer.files)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async (e: FormEvent) => {
    e.preventDefault()
    if (selectedFiles.length === 0) { setMessage('Wybierz pliki.'); return }

    setUploading(true)
    setMessage('')
    setUploadProgress({ current: 0, total: selectedFiles.length })

    let successCount = 0
    let errorMsg = ''

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i]
      setUploadProgress({ current: i + 1, total: selectedFiles.length })

      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}_${i}.${fileExt}`
      const { error: uploadError } = await supabase.storage.from('gallery').upload(fileName, file, { contentType: file.type })

      if (uploadError) {
        errorMsg = `Błąd uploadu ${file.name}: ${uploadError.message}`
        continue
      }

      const { data: urlData } = supabase.storage.from('gallery').getPublicUrl(fileName)
      const { error: dbError } = await supabase.from('gallery_images').insert({
        image_url: urlData.publicUrl,
        caption: caption.trim() || file.name,
        album_id: selectedAlbumId || null,
      })

      if (dbError) {
        errorMsg = `Błąd bazy dla ${file.name}: ${dbError.message}`
        continue
      }

      successCount++
    }

    setCaption('')
    setSelectedAlbumId('')
    setSelectedFiles([])
    setUploadProgress({ current: 0, total: 0 })

    if (errorMsg) {
      setMessage(`Wysłano ${successCount} z ${selectedFiles.length}. ${errorMsg}`)
    } else {
      setMessage(`Wysłano ${successCount} zdjęć!`)
    }

    fetchAll()
    setUploading(false)
  }

  const handleDeleteImage = async (id: string, url: string) => {
    if (!confirm('Na pewno usunąć to zdjęcie?')) return
    const fileName = url.split('/').pop()
    if (fileName) await supabase.storage.from('gallery').remove([fileName])
    const { error } = await supabase.from('gallery_images').delete().eq('id', id)
    if (!error) { setImages((prev) => prev.filter((img) => img.id !== id)) }
  }

  const handleDeleteAlbum = async (albumId: string) => {
    if (!confirm('Na pewno usunąć ten album? Zdjęcia pozostaną w galerii bez przypisanego albumu.')) return
    await supabase.from('gallery_albums').delete().eq('id', albumId)
    fetchAll()
  }

  const toggleAlbum = (id: string) => {
    setExpandedAlbums((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const imagesByAlbum = (albumId: string | null) =>
    images.filter((img) => img.album_id === albumId)

  const ungroupedImages = images.filter((img) => !img.album_id)

  return (
    <Layout>
      {!session ? (
        <div className="min-h-[70vh] flex items-center justify-center px-4 py-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <div className="bg-[#1A1A1A]/90 border border-military/20 rounded-sm p-8">
              <div className="text-center mb-8">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-military/20 rounded-full">
                  <Lock size={24} className="text-military-light" />
                </div>
                <h1 className="font-display text-3xl text-bone tracking-wider">PANEL ADMINA</h1>
                <p className="font-body text-chrome text-sm mt-2">Zaloguj się, aby zarządzać galerią.</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block font-body text-sm text-chrome mb-1">Email</label>
                  <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required
                    className="w-full px-4 py-2 bg-steel border border-military/30 rounded-sm text-bone font-body placeholder:text-chrome/40 focus:border-military-light focus:outline-none" placeholder="admin@strefatop.pl" />
                </div>
                <div>
                  <label className="block font-body text-sm text-chrome mb-1">Hasło</label>
                  <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required
                    className="w-full px-4 py-2 bg-steel border border-military/30 rounded-sm text-bone font-body placeholder:text-chrome/40 focus:border-military-light focus:outline-none" placeholder="••••••••" />
                </div>
                {loginError && <p className="font-body text-sm text-red-400">{loginError}</p>}
                <button type="submit" disabled={loginLoading}
                  className="w-full py-3 bg-military-light text-bone font-subheading font-bold uppercase tracking-wider rounded-sm hover:bg-military-glow transition-colors disabled:opacity-50">
                  {loginLoading ? 'Logowanie...' : 'Zaloguj się'}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      ) : (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
          <div className="flex items-center justify-end mb-8">
            <div className="flex items-center gap-4">
              <span className="font-body text-sm text-chrome hidden sm:inline">{session.user.email}</span>
              <button onClick={handleLogout}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-400/30 text-red-400 font-subheading text-sm uppercase tracking-wider rounded-sm hover:bg-red-400/10 transition-colors">
                <LogOut size={14} /> Wyloguj
              </button>
            </div>
          </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="font-display text-4xl sm:text-5xl text-bone tracking-wider">PANEL GALERII</h1>
          <p className="font-body text-chrome mt-2">Twórz albumy, dodawaj zdjęcia z podpisem — wszystko automatycznie pojawia się na stronie.</p>
        </motion.div>

        {/* Create Album */}
        <div className="bg-[#1A1A1A]/80 border border-military/20 rounded-sm p-6 mb-8">
          <button onClick={() => setShowNewAlbum(!showNewAlbum)}
            className="flex items-center gap-2 font-subheading font-bold text-bone text-lg hover:text-military-light transition-colors">
            <FolderPlus size={20} className="text-military-light" />
            Nowy album
            {showNewAlbum ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          <AnimatePresence>
            {showNewAlbum && (
              <motion.form initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mt-4 space-y-4"
                onSubmit={handleCreateAlbum}>
                <div>
                  <label className="block font-body text-sm text-chrome mb-1">Nazwa albumu</label>
                  <input type="text" value={newAlbumName} onChange={(e) => setNewAlbumName(e.target.value)} required
                    placeholder="np. Integracja firmowa 2026"
                    className="w-full px-4 py-2 bg-steel border border-military/30 rounded-sm text-bone font-body placeholder:text-chrome/40 focus:border-military-light focus:outline-none" />
                </div>
                <div>
                  <label className="block font-body text-sm text-chrome mb-1">Opis (opcjonalnie)</label>
                  <input type="text" value={newAlbumDesc} onChange={(e) => setNewAlbumDesc(e.target.value)}
                    placeholder="Krótki opis albumu"
                    className="w-full px-4 py-2 bg-steel border border-military/30 rounded-sm text-bone font-body placeholder:text-chrome/40 focus:border-military-light focus:outline-none" />
                </div>
                <button type="submit"
                  className="inline-flex items-center gap-2 px-6 py-2 bg-military-light text-bone font-subheading font-bold uppercase tracking-wider rounded-sm hover:bg-military-glow transition-colors">
                  <FolderPlus size={16} /> Utwórz album
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        {/* Upload Form */}
        <div className="bg-[#1A1A1A]/80 border border-military/20 rounded-sm p-6 mb-12">
          <h2 className="font-subheading font-bold text-bone text-lg mb-4 flex items-center gap-2">
            <ImagePlus size={20} className="text-military-light" /> Dodaj zdjęcia
          </h2>
          <form onSubmit={handleUpload} className="space-y-4">
            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-military-light bg-military/10'
                  : 'border-military/30 hover:border-military-light hover:bg-military/5'
              }`}
            >
              <Upload size={32} className="mx-auto mb-3 text-chrome" />
              <p className="font-body text-bone mb-1">Przeciągnij zdjęcia tutaj lub kliknij, aby wybrać</p>
              <p className="font-body text-xs text-chrome">Możesz wybrać wiele plików naraz (JPG, PNG, WEBP)</p>
              <input
                key={fileInputKey}
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Selected files list */}
            {selectedFiles.length > 0 && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="bg-military/10 border border-military-light/30 rounded-sm p-4">
                <p className="font-subheading text-sm text-military-light mb-3 flex items-center gap-2">
                  <FileImage size={16} /> Wybrano {selectedFiles.length} {selectedFiles.length === 1 ? 'plik' : selectedFiles.length < 5 ? 'pliki' : 'plików'}
                </p>
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {selectedFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} className="flex items-center justify-between gap-2 bg-steel/70 rounded-sm px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileImage size={14} className="text-military-light flex-shrink-0" />
                        <span className="font-body text-sm text-bone truncate">{file.name}</span>
                        <span className="font-mono text-[10px] text-chrome flex-shrink-0">({(file.size / 1024).toFixed(0)} KB)</span>
                      </div>
                      <button type="button" onClick={() => removeFile(idx)}
                        className="p-1 text-red-400 hover:bg-red-400/10 rounded-sm transition-colors flex-shrink-0">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div>
              <label className="block font-body text-sm text-chrome mb-1">Podpis / opis (dla wszystkich zdjęć)</label>
              <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
                placeholder="Jeśli pusty — użyje nazwy pliku"
                className="w-full px-4 py-2 bg-steel border border-military/30 rounded-sm text-bone font-body placeholder:text-chrome/40 focus:border-military-light focus:outline-none" />
            </div>
            <div>
              <label className="block font-body text-sm text-chrome mb-1">Album (opcjonalnie)</label>
              <select value={selectedAlbumId} onChange={(e) => setSelectedAlbumId(e.target.value)}
                className="w-full px-4 py-2 bg-steel border border-military/30 rounded-sm text-bone font-body focus:border-military-light focus:outline-none">
                <option value="">— Bez albumu —</option>
                {albums.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
              </select>
            </div>
            {message && (
              <p className={`font-body text-sm ${message.includes('Błąd') ? 'text-red-400' : 'text-military-light'}`}>{message}</p>
            )}
            {uploading && (
              <p className="font-body text-sm text-chrome">
                Wysyłanie {uploadProgress.current} z {uploadProgress.total}...
              </p>
            )}
            <button type="submit" disabled={uploading || selectedFiles.length === 0}
              className="inline-flex items-center gap-2 px-6 py-2 bg-military-light text-bone font-subheading font-bold uppercase tracking-wider rounded-sm hover:bg-military-glow transition-colors disabled:opacity-50">
              <Upload size={16} /> {uploading ? 'Wysyłanie...' : `Wyślij ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}`}
            </button>
          </form>
        </div>

        {/* Albums & Images */}
        <div>
          <h2 className="font-subheading font-bold text-bone text-lg mb-4">Albumy i zdjęcia</h2>
          {loading ? (
            <p className="text-chrome font-body">Ładowanie...</p>
          ) : (
            <div className="space-y-6">
              {/* Albums */}
              {albums.map((album) => {
                const albumImages = imagesByAlbum(album.id)
                const isExpanded = expandedAlbums.has(album.id)
                return (
                  <div key={album.id} className="bg-[#1A1A1A]/80 border border-military/20 rounded-sm">
                    <div onClick={() => toggleAlbum(album.id)}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAlbum(album.id) } }}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-military/5 transition-colors cursor-pointer">
                      <div className="flex items-center gap-3">
                        <Folder size={20} className="text-military-light" />
                        <div>
                          <p className="font-subheading font-bold text-bone">{album.name}</p>
                          {album.description && <p className="font-body text-sm text-chrome">{album.description}</p>}
                          <p className="font-mono text-[10px] text-chrome mt-1">{albumImages.length} zdjęć</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteAlbum(album.id) }}
                          className="p-2 text-red-400 hover:bg-red-400/10 rounded-sm transition-colors">
                          <Trash2 size={14} />
                        </button>
                        {isExpanded ? <ChevronUp size={18} className="text-chrome" /> : <ChevronDown size={18} className="text-chrome" />}
                      </div>
                    </div>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <div className="px-4 pb-4">
                            {albumImages.length === 0 ? (
                              <p className="font-body text-sm text-chrome py-2">Brak zdjęć w tym albumie.</p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {albumImages.map((img) => (
                                  <div key={img.id} className="flex gap-3 bg-steel/50 border border-military/10 rounded-sm p-2">
                                    <img src={img.image_url} alt={img.caption} className="w-20 h-20 object-cover rounded-sm flex-shrink-0 bg-white" />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-body text-sm text-bone truncate">{img.caption}</p>
                                      <p className="font-mono text-[10px] text-chrome mt-1">{new Date(img.created_at).toLocaleDateString('pl-PL')}</p>
                                      <button onClick={() => handleDeleteImage(img.id, img.image_url)}
                                        className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-subheading text-red-400 border border-red-400/30 rounded-sm hover:bg-red-400/10 transition-colors">
                                        <Trash2 size={10} /> Usuń
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}

              {/* Ungrouped Images */}
              {ungroupedImages.length > 0 && (
                <div className="bg-[#1A1A1A]/80 border border-military/20 rounded-sm p-4">
                  <p className="font-subheading font-bold text-bone mb-3">Zdjęcia bez albumu ({ungroupedImages.length})</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {ungroupedImages.map((img) => (
                      <div key={img.id} className="flex gap-3 bg-steel/50 border border-military/10 rounded-sm p-2">
                        <img src={img.image_url} alt={img.caption} className="w-20 h-20 object-cover rounded-sm flex-shrink-0 bg-white" />
                        <div className="flex-1 min-w-0">
                          <p className="font-body text-sm text-bone truncate">{img.caption}</p>
                          <p className="font-mono text-[10px] text-chrome mt-1">{new Date(img.created_at).toLocaleDateString('pl-PL')}</p>
                          <button onClick={() => handleDeleteImage(img.id, img.image_url)}
                            className="mt-2 inline-flex items-center gap-1 px-2 py-1 text-xs font-subheading text-red-400 border border-red-400/30 rounded-sm hover:bg-red-400/10 transition-colors">
                            <Trash2 size={10} /> Usuń
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )}
  </Layout>
  )
}
